<script setup lang="ts">
import { VDateInput } from 'vuetify/labs/VDateInput'
import { notifyError, toDate_wd_dd_MMMM_hh_mm /*, roundTo*/ } from '@/utils/helpers'
import type MDBVegcoCommandeVente from '@/types/vegco/commandevente'

const storeU = useUtilisateurStore()
const dialog = ref(false)
const modifications = ref(false)

const commande = ref({} as MDBVegcoCommandeVente)
const commandeSource = ref({} as MDBVegcoCommandeVente)
const aUneDateCoupéManuelle = ref(false)

const dateCréation = computed(() => toDate_wd_dd_MMMM_hh_mm(commande.value.dateCréation))
const dateProduction = computed(() => toDate_wd_dd_MMMM_hh_mm(commande.value.dateProduction))
const dateChargé = computed(() => toDate_wd_dd_MMMM_hh_mm(commande.value.dateChargé))
const dateLivré = computed(() => toDate_wd_dd_MMMM_hh_mm(commande.value.dateLivré))

let resolve: (value: boolean) => void
watch(dialog, (v) => {
  if (!v) {
    resolve(modifications.value)
  }
})

function openCommande(commandeEdit: MDBVegcoCommandeVente) {
  commandeSource.value = commandeEdit
  commande.value = { ... commandeEdit }
  modifications.value = false

  aUneDateCoupéManuelle.value = true
  if (new Date(commande.value.dateCoupéManuelle).getFullYear() <= 2000) {
    aUneDateCoupéManuelle.value = false
    commande.value.dateCoupéManuelle = '2000-01-01T00:00:00Z'
  }

  dialog.value = true
  return new Promise<boolean>((resolveFn) => {
    resolve = resolveFn
  })
}

function aUneModification() {
  if (!aUneDateCoupéManuelle.value) {
    commande.value.dateCoupéManuelle = '2000-01-01T00:00:00Z'
  }
  else {
    const date = new Date(commande.value.dateCoupéManuelle)
    date.setHours(12)
    commande.value.dateCoupéManuelle = date.toISOString()
  }

  if (commandeSource.value.dateCoupéManuelle != commande.value.dateCoupéManuelle)
    return true

  return false
}

async function confirm() {
  try
  {
    if (!aUneModification()) {
      dialog.value = false
      return
    }

    await apiProphet.put(`vgco/commandes/vente/${commande.value.id}`, commande.value)
    Notify.success('Modification de la commande avec succès');

    modifications.value = true
    dialog.value = false
  }
  catch (e) {
    notifyError(e)
  }
}

function cancel() {
  dialog.value = false
}

function dateCoupéManuelleChange() {
  aUneDateCoupéManuelle.value = !aUneDateCoupéManuelle.value
  if (!aUneDateCoupéManuelle.value) {
    commande.value.dateCoupéManuelle = '2000-01-01T00:00:00Z'
  } else {
    commande.value.dateCoupéManuelle = commande.value.dateChargé
  }
}

onMounted(async () => {
  if (!storeU.siteSélectionné) {
    Notify.error('Veuillez sélectionner un site')
    cancel()
    return
  }
})

defineExpose({ openCommande })
</script>

<template>
  <v-dialog v-model="dialog" max-width="1000px" :fullscreen="$vuetify.display.smAndDown">
    <v-card style="z-index: -1">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5 text-medium-emphasis ps-2">Modifier commande</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="cancel"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-container/>
          <v-row>
            <v-col cols="12" md="4" sm="4">
              <v-text-field v-model="commande.client" label="Client" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="commande.clientSite" label="Site du client" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="commande.noCommande" label="Numéro" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="commande.statut" label="Statut" readonly density="compact" variant="outlined"/>
            </v-col>
            <v-col cols="12" md="4" sm="4">
              <v-text-field v-model="dateCréation" label="Date création" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="dateProduction" label="Date production" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="dateChargé" label="Date chargé" readonly density="compact" variant="outlined"/>
              <v-text-field v-model="dateLivré" label="Date livré" readonly density="compact" variant="outlined"/>
            </v-col>
            <v-col v-if="commande.statut != 'Inventaire'" cols="12" md="4" sm="4">
              <v-date-input
                v-if="aUneDateCoupéManuelle" v-model="commande.dateCoupéManuelle" label="Date coupé manuelle"
                display-format="normalDateWithWeekday" prepend-icon=""/>
              <v-btn
                color="secondary"
                variant="flat"
                :text="aUneDateCoupéManuelle ? 'Supprimer date coupé manuelle' : 'Ajouter date coupé manuelle'"
                @click="dateCoupéManuelleChange"/>
            </v-col>
          </v-row>
      </v-card-text>

      <v-divider class="mt-2"/>

      <v-card-actions class="d-flex justify-end mr-2 my-2">
        <v-btn @click="cancel">Annuler</v-btn>
        <v-btn color="primary" variant="flat" @click="confirm">Modifier la commande</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
